import React, { useEffect, useState, useRef } from "react";
import { Table, Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import { TABLE_DATE_FORMAT } from "../../../../common/constants";

import { getOrderTaxSummary } from "../../../../utils/orderTaxes";

import { getBusinessOrderTaxes } from "../../../../utils/businessOrderTaxes";

import OrderTaxRows from "../../../../common/OrderTaxRows";
import OrderTipRow from "../../../../common/OrderTipRow";

export default function OrderInfo(props) {
  const { order_id, businessID } = props;

  const [orderDetails, setOrderDetails] = useState();
  const printRef = useRef(null);
  const [taxSummary, setTaxSummary] = useState(getOrderTaxSummary());
  const [loadingTaxes, setLoadingTaxes] = useState(false);
  const [taxError, setTaxError] = useState("");

  const dateFormat = TABLE_DATE_FORMAT;

  const isDelivery =
    (orderDetails?.delivery_type || "").toLowerCase() === "delivery";

  const buyerEmail =
    orderDetails?.customer_email ||
    orderDetails?.email ||
    orderDetails?.buyer_email ||
    orderDetails?.user_email;

  const buyerPhone =
    orderDetails?.customer_phone ||
    orderDetails?.phone ||
    orderDetails?.buyer_phone ||
    orderDetails?.user_phone ||
    orderDetails?.delivery_phone_client;

  const buyerName =
    [
      orderDetails?.customer_name,
      orderDetails?.customer_lastname || orderDetails?.customer_last_name,
    ]
      .filter(Boolean)
      .join(" ") ||
    orderDetails?.buyer_name ||
    orderDetails?.user_name ||
    orderDetails?.name ||
    orderDetails?.delivery_name_client;

  const deliveryAddressParts = [
    orderDetails?.delivery_address,
    orderDetails?.delivery_city,
    orderDetails?.delivery_state,
    orderDetails?.delivery_zip,
  ].filter(Boolean);
  const deliveryAddressText = deliveryAddressParts.join(", ");
  const deliveryAddressDisplay =
    deliveryAddressText ||
    orderDetails?.delivery_address ||
    "No address provided";

  const columns = [
    {
      title: "Dish Name",
      key: "dish_name",
      render: (text) => <>{text.dish_name}</>,
    },
    {
      title: "Quantity",
      key: "quantity",
      render: (text) => <>{text.dish_quantity} </>,
    },
    {
      title: "Total",
      key: "total",
      render: (text) => <>$ {text.dish_price * text.dish_quantity} </>,
    },
  ];

  useEffect(() => {
    let cancelled = false;
    setOrderDetails(undefined);
    const getOrderDetails = async () => {
      try {
        const response = await axiosWithAuth().get(
          `/api/restaurant/order/getOrdersDetails/${order_id}`
        );
        if (!cancelled) setOrderDetails(response.data.data.order);
      } catch (error) {
        console.error("Error getting orders:", error);
      }
    };
    getOrderDetails();
    return () => { cancelled = true; };
  }, [order_id]);

  useEffect(() => {
    let cancelled = false;
    const summary = getOrderTaxSummary(orderDetails);
    setTaxSummary(summary);
    setTaxError("");
    setLoadingTaxes(false);
    if (!orderDetails || summary.fees?.length) return;

    const idBusiness = orderDetails.businesses_id ?? orderDetails.business_id ??
      orderDetails.restaurant_id ?? businessID;
    if (!idBusiness) {
      setTaxError("Unable to identify the business for this order.");
      return;
    }
    const getBusinessTaxes = async () => {
      setLoadingTaxes(true);
      const APIURL = `/api/business/getBusinessTaxes/${idBusiness}`;
      try {
        const response = await axiosWithAuth().get(APIURL);
        const taxes = getBusinessOrderTaxes(orderDetails, response.data);
        if (!cancelled) {
          setTaxSummary(taxes);
          if (!taxes.fees) setTaxError("Order subtotal is unavailable.");
        }
      } catch (error) {
        console.error("Error getting business taxes:", error);
        if (!cancelled) setTaxError("Unable to load business taxes. Please reload to try again.");
      } finally {
        if (!cancelled) setLoadingTaxes(false);
      }
    };
    getBusinessTaxes();
    return () => { cancelled = true; };
  }, [orderDetails, businessID]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Order-${order_id}`,
  });

  return (
    <>
      {orderDetails && (
        <>
          <div style={{ marginBottom: "20px" }}>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              size="large"
            >
              Print Order
            </Button>
          </div>

          <div ref={printRef}>
            <div
              className="print-only-header"
              style={{ textAlign: "center", marginBottom: "30px" }}
            >
              <h1
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "28px",
                  fontWeight: "bold",
                }}
              >
                BIZZLL
              </h1>
              <h2
                style={{ margin: "0", fontSize: "20px", fontWeight: "normal" }}
              >
                Order Receipt
              </h2>
            </div>

            <div
              style={{
                marginBottom: "20px",
                padding: "15px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: "16px",
                }}
              >
                <div>
                  <p style={{ margin: "5px 0", fontSize: "16px" }}>
                    <strong>Order Number:</strong> #{order_id}
                  </p>
                  <p style={{ margin: "5px 0", fontSize: "16px" }}>
                    <strong>Date:</strong>{" "}
                    {moment(new Date(orderDetails.date_order)).format(
                      dateFormat
                    )}
                  </p>
                  <p style={{ margin: "5px 0", fontSize: "16px" }}>
                    <strong>Status:</strong> {orderDetails.order_status}
                  </p>
                  <p style={{ margin: "5px 0", fontSize: "16px" }}>
                    <strong>Type:</strong> {orderDetails.delivery_type || "N/A"}
                  </p>
                </div>

                <div>
                  {(buyerName || buyerPhone || buyerEmail) && (
                    <h4
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      Buyer Information
                    </h4>
                  )}
                  {buyerName && (
                    <p style={{ margin: "5px 0", fontSize: "16px" }}>
                      <strong>Name:</strong> {buyerName}
                    </p>
                  )}
                  {buyerPhone && (
                    <p style={{ margin: "5px 0", fontSize: "16px" }}>
                      <strong>Phone:</strong> {buyerPhone}
                    </p>
                  )}
                  {buyerEmail && (
                    <p style={{ margin: "5px 0", fontSize: "16px" }}>
                      <strong>Email:</strong> {buyerEmail}
                    </p>
                  )}
                </div>

                {isDelivery && (
                  <div>
                    <h4
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      Delivery Details
                    </h4>
                    <p style={{ margin: "5px 0", fontSize: "16px" }}>
                      <strong>Address:</strong> {deliveryAddressDisplay}
                    </p>
                    {orderDetails.delivery_reference && (
                      <p style={{ margin: "5px 0", fontSize: "16px" }}>
                        <strong>Reference:</strong>{" "}
                        {orderDetails.delivery_reference}
                      </p>
                    )}
                    {orderDetails.delivery_instructions && (
                      <p style={{ margin: "5px 0", fontSize: "16px" }}>
                        <strong>Instructions:</strong>{" "}
                        {orderDetails.delivery_instructions}
                      </p>
                    )}
                    {orderDetails.delivery_contact && (
                      <p style={{ margin: "5px 0", fontSize: "16px" }}>
                        <strong>Contact:</strong>{" "}
                        {orderDetails.delivery_contact}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <h3
              style={{
                marginBottom: "15px",
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              Order Items
            </h3>

            <Table
              columns={columns}
              dataSource={orderDetails.detail}
              pagination={false}
              className="no-print-pagination"
            />

            <div
              style={{
                marginTop: "30px",
                padding: "20px",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              {orderDetails.delivery_price > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    fontSize: "16px",
                  }}
                >
                  <span>
                    <strong>Delivery Fee:</strong>
                  </span>
                  <span>${Number(orderDetails.delivery_price).toFixed(2)}</span>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  fontSize: "16px",
                }}
              >
                <span>
                  <strong>Subtotal:</strong>
                </span>
                <span>
                  ${taxSummary.subtotal?.toFixed(2)}
                </span>
              </div>
              <section aria-label="Taxes and Fees">
                <OrderTaxRows summary={taxSummary} />
                {loadingTaxes && <p>Loading business taxes...</p>}
                {taxError && <p role="alert">{taxError}</p>}
              </section>
              <OrderTipRow tip={taxSummary.tip} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "15px 0",
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
              >
                <span>TOTAL:</span>
                <span>
                  ${Number(orderDetails.total_price_order).toFixed(2)}
                </span>
              </div>
            </div>

            {orderDetails.notes && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "15px",
                  backgroundColor: "#fff9e6",
                  borderRadius: "8px",
                  borderLeft: "4px solid #ffa500",
                }}
              >
                <p style={{ margin: "0", fontSize: "14px" }}>
                  <strong>Notes:</strong> {orderDetails.notes}
                </p>
              </div>
            )}

            <div
              className="print-only-footer"
              style={{
                marginTop: "40px",
                textAlign: "center",
                fontSize: "12px",
                color: "#666",
              }}
            >
              <p>Thank you for your business!</p>
              <p>Printed on {moment().format("MMMM DD, YYYY [at] HH:mm")}</p>
            </div>
          </div>

          <style>
            {`
              @media print {
                @page {
                  margin: 10mm;
                }
                .ant-table-pagination {
                  display: none !important;
                }
                .ant-btn {
                  display: none !important;
                }
                body {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                  margin: 0;
                  padding: 10mm;
                }
              }
              @media screen {
                .print-only-header,
                .print-only-footer {
                  display: none;
                }
              }
            `}
          </style>
        </>
      )}
    </>
  );
}
